import { useCallback } from 'react';

// import {isFocusEvent, isPointerEvent} from '../typeguards/events';

import findNearestDatumX from '../utils/findNearestDatumX';
import findNearestDatumY from '../utils/findNearestDatumY';
import useEventEmitter from './useEventEmitter';
export const POINTER_EVENTS_ALL = '__POINTER_EVENTS_ALL';
export const POINTER_EVENTS_NEAREST = '__POINTER_EVENTS_NEAREST';
// functional definition of a PointerEvent (mouse, touch)
export function isPointerEvent(event) {
  return !!event && ('clientX' in event || 'changedTouches' in event);
}
export function isFocusEvent(event) {
  return !!event && !isPointerEvent(event);
}
/**
 * Hook that returns PointerEvent handlers that invoke the passed pointer
 * handlers with the nearest datum to the event for the passed dataKey.
 */
export default function usePointerEventHandlers(_ref) {
  let {
    findNearestDatum: findNearestDatumProps,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    allowedSources,
    width,
    height,
    margin,
    data,
    xScale,
    yScale,
    xAccessor,
    yAccessor,
    seriesData,
    horizontal
  } = _ref;
  // this logic is shared by pointerup, pointermove, and focus handlers
  const getHandlerParams = useCallback(params => {
    const {
      svgPoint,
      event
    } = params || {};

    // nearest Datum across all dataKeys, if relevant
    // let nearestDatumPointerParams: EventHandlerParams<Datum> | null = null;
    // let nearestDatumDistance = Infinity;

    if (params && event && svgPoint && width && height) {
      const findNearestFunction = !horizontal ? findNearestDatumX : findNearestDatumY;
      const nearestDatum = findNearestFunction({
        height,
        data,
        point: {
          x: svgPoint.x - margin.left,
          y: svgPoint.y - margin.top
        },
        width,
        xAccessor: !horizontal ? xAccessor : yAccessor,
        xScale,
        yAccessor: !horizontal ? yAccessor : xAccessor,
        yScale
      });
      if (nearestDatum) {
        const access = xAccessor;
        // TODO: get all of the data nearest
        const pointerParams = [{
          svgPoint,
          event,
          ...nearestDatum,
          nearestSeriesData: seriesData.get(access(nearestDatum === null || nearestDatum === void 0 ? void 0 : nearestDatum.datum))
        }];
        // dataKey === POINTER_EVENTS_NEAREST
        // console.log(pointerParams);
        //     ? [nearestDatumPointerParams]
        // : dataKey === POINTER_EVENTS_ALL || Array.isArray(dataKey)
        //   ? Object.values(pointerParamsByKey)
        //   : [pointerParamsByKey[dataKey]];

        return pointerParams.filter(param => param);
      } else {
        const pointerParams = [{
          svgPoint,
          event,
          datum: null
        }];
        return pointerParams.filter(param => param);
      }
    }
    return [];
  }, [data, xScale, yScale, width, height, findNearestDatumX]);
  const handlePointerMove = useCallback(params => {
    if (onPointerMove) {
      getHandlerParams(params).forEach(p => onPointerMove(p));
    }
  }, [getHandlerParams, onPointerMove]);
  const handlePointerUp = useCallback(params => {
    if (onPointerUp) {
      getHandlerParams(params).forEach(p => onPointerUp(p));
    }
  }, [getHandlerParams, onPointerUp]);
  const handlePointerDown = useCallback(params => {
    if (onPointerDown) {
      getHandlerParams(params).forEach(p => onPointerDown(p));
    }
  }, [getHandlerParams, onPointerDown]);
  const handleFocus = useCallback(params => {
    if (onFocus) {
      getHandlerParams(params).forEach(p => onFocus(p));
    }
  }, [getHandlerParams, onFocus]);
  const handlePointerOut = useCallback(params => {
    const event = params === null || params === void 0 ? void 0 : params.event;
    if (event && isPointerEvent(event) && onPointerOut) onPointerOut(event);
  }, [onPointerOut]);
  const handleBlur = useCallback(params => {
    const event = params === null || params === void 0 ? void 0 : params.event;
    if (event && isFocusEvent(event) && onBlur) onBlur(event);
  }, [onBlur]);
  useEventEmitter('pointermove', onPointerMove ? handlePointerMove : undefined, allowedSources);
  useEventEmitter('pointerout', onPointerOut ? handlePointerOut : undefined, allowedSources);
  useEventEmitter('pointerup', onPointerUp ? handlePointerUp : undefined, allowedSources);
  useEventEmitter('pointerdown', onPointerDown ? handlePointerDown : undefined, allowedSources);
  useEventEmitter('focus', onFocus ? handleFocus : undefined, allowedSources);
  useEventEmitter('blur', onBlur ? handleBlur : undefined, allowedSources);
}